import React from 'react';
import { CustomImage as Image } from 'components/index';
import styles from './BentoCard.module.css';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';

type BackgroundProps =
  | { bgColor: string; bgGradient?: never; bgImage?: never }
  | { bgGradient: string; bgColor?: never; bgImage?: never }
  | { bgImage: string; bgColor?: never; bgGradient?: never }
  | { bgColor?: undefined; bgGradient?: undefined; bgImage?: undefined };

type BentoCardStyledProps = {
  $parentType: 'grid' | 'flex';
  $textAlign?: 'left' | 'center' | 'right';
  $colReverse?: boolean;
  $textColor?: string;
  $gridColumn?: string;
  $gridRow?: string;
  $imagePosition?: 'top' | 'bottom' | 'center';
  $bgColor?: string;
  $bgGradient?: string;
  $bgImage?: string;
};

export type BentoCardProps = BentoCardBaseProps & BentoCardStyledProps;

type BentoCardBaseProps = {
  className?: string;
  parentType: 'grid' | 'flex';
  caption: string;
  title: string | React.ReactNode;
  textAlign?: 'left' | 'center' | 'right';
  colReverse?: boolean;
  textColor?: string;
  gridColumn?: string;
  gridRow?: string;
  image?: string;
  alt?: string;
  imagePosition?: 'top' | 'bottom' | 'center';
} & BackgroundProps;

export const BentoCard = React.forwardRef<HTMLDivElement, BentoCardBaseProps>(
  (props, ref) => {
    const {
      className,
      parentType,
      caption,
      title,
      textAlign,
      colReverse,
      textColor,
      gridColumn,
      gridRow,
      image,
      alt,
      imagePosition,
      bgColor,
      bgGradient,
      bgImage,
    } = props;

    const style: Record<string, string> = {
      '--bento-flex-direction': colReverse ? 'column-reverse' : 'column',
    };
    if (gridColumn) style['--bento-grid-column'] = gridColumn;
    if (gridRow) style['--bento-grid-row'] = gridRow;

    let bg: 'color' | 'gradient' | 'image' | undefined;
    if (bgColor) {
      bg = 'color';
      style['--bento-bg'] = bgColor;
    } else if (bgGradient) {
      bg = 'gradient';
      // A trailing ';' is invalid in a custom-property value set via CSSOM and
      // silently voids the whole declaration — strip it defensively.
      style['--bento-bg'] = bgGradient.replace(/;\s*$/, '');
    } else if (bgImage) {
      bg = 'image';
      style['--bento-bg'] = `url(${getCompressedAssetUrl(bgImage)})`;
    }

    return (
      <div
        ref={ref}
        className={
          className ? `${styles.bentoCard} ${className}` : styles.bentoCard
        }
        data-parent-type={parentType}
        data-bg={bg}
        style={style as React.CSSProperties}
      >
        <TextContainer $textAlign={textAlign} $textColor={textColor}>
          <Caption>{caption}</Caption>
          <Title>{title}</Title>
        </TextContainer>
        {image ? (
          <ImageContainer $imagePosition={imagePosition}>
            <Image
              src={image}
              alt={alt || 'Ultrahuman Home - features image'}
              width={600}
              height={400}
            />
          </ImageContainer>
        ) : null}
      </div>
    );
  },
);

BentoCard.displayName = 'BentoCard';

const TextContainer = ({
  $textAlign,
  $textColor,
  children,
}: {
  $textAlign?: 'left' | 'center' | 'right';
  $textColor?: string;
  children: React.ReactNode;
}) => (
  <div
    className={styles.textContainer}
    data-text-align={$textAlign}
    style={
      $textColor
        ? ({ ['--text-color']: $textColor } as React.CSSProperties)
        : undefined
    }
  >
    {children}
  </div>
);

const Caption = ({ children }: { children: React.ReactNode }) => (
  <p className={styles.caption}>{children}</p>
);

const Title = ({ children }: { children: React.ReactNode }) => (
  <h3 className={styles.title}>{children}</h3>
);

const ImageContainer = ({
  $imagePosition,
  children,
}: {
  $imagePosition?: 'top' | 'bottom' | 'center';
  children: React.ReactNode;
}) => (
  <div
    className={styles.imageContainer}
    style={
      $imagePosition
        ? ({ ['--image-position']: $imagePosition } as React.CSSProperties)
        : undefined
    }
  >
    {children}
  </div>
);
